import CheckIcon from "@mui/icons-material/Check";
import { Link, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { useContext, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

import Flag from "@/components/Icons/Flag";
import { ampli } from "@/config";
import { getWorkspaceLanguages } from "@/data/workspaceConfig";
import { DataContext } from "@/models/DataProvider";
import { languageFilter, type PreferredLanguage } from "@/models/languages";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";

export default function LanguageDropButton() {
  const { preferredLanguage, setPreferredLanguage } = React.useContext(DataContext);
  const wrapperRef = React.useRef(null);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const availableLanguages = getWorkspaceLanguages(workspaceId);
  const workspaceLanguages = languageFilter(availableLanguages);
  const hasActiveLanguage = workspaceLanguages?.find((lang) => lang?.value === preferredLanguage);
  if (!hasActiveLanguage && workspaceLanguages?.length > 0) {
    setPreferredLanguage(workspaceLanguages[0].value as PreferredLanguage);
  }
  const [menuStatus, setMenuStatus] = useState<boolean>(false);
  const toggleMenuStatus = () => {
    ampli.languageModal();
    setMenuStatus((value) => !value);
  };
  const changeLanguage = (value: string) => {
    const translationLanguage = workspaceLanguages.find((lang) => lang.value === value)?.text ?? "none";
    ampli.updateUserLanguage({ translationLanguage });
    setPreferredLanguage(value as PreferredLanguage);
    toggleMenuStatus();
  };
  useOnClickOutside(wrapperRef, () => setMenuStatus(false));

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }} ref={wrapperRef}>
      <Box
        onClick={toggleMenuStatus}
        sx={{
          cursor: "pointer",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Flag width={24} locale={preferredLanguage} />
      </Box>
      <Box
        sx={{
          display: menuStatus ? "block" : "none",
          position: "fixed",
          left: "70px",
          bottom: "10px",
          bgcolor: "var(--color-zinc-800)",
          width: "200px",
          p: 1,
          border: "2px solid var(--color-zinc-500)",
          borderRadius: 1,
          boxShadow: "0 0 0 2px rgba(0,0,0,0.5)",
          zIndex: 30,
        }}
      >
        <Box>
          {workspaceLanguages.map((language) => (
            <Box sx={{ mb: 1 }} key={language.value}>
              <Box
                component="button"
                onClick={() => {
                  changeLanguage(language.value);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "100%",
                  "&:hover": { bgcolor: "var(--color-zinc-700)" },
                  p: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  border: "none",
                  bgcolor: "transparent",
                  color: "inherit",
                }}
              >
                <span>
                  <Flag width={24} locale={language.value} title={language.text} />
                </span>
                <Box component="span" sx={{ fontSize: "0.875rem", textAlign: "left", flexGrow: 1 }}>
                  {language.text}
                </Box>
                <Box component="span" sx={{ width: "25px" }}>
                  {language.value === preferredLanguage ? <CheckIcon /> : ""}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        <Typography
          sx={{
            px: 1,
            mt: 1,
            fontSize: "11px",
            color: "#999",
          }}
        >
          All message transcriptions will display in the chosen language.{" "}
          <Link target="_blank" sx={{ color: "#999" }} href="https://storyboard.com/transcription-translation-policy">
            Language Policy
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
